import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Landmark, Smartphone, ShieldCheck, TriangleAlert, Plus, Eye } from "lucide-react";

import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useListComptesReversement,
  useCreateCompteReversement,
  useUpdateCompteReversement,
  useVerifierCompteReversement,
  useDesactiverCompteReversement,
} from "@/lib/backoffice-hooks";
import type { CompteReversement } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * État du formulaire : les champs texte y sont toujours des chaînes, jamais
 * `undefined`. Sans cela, chaque lecture devait porter un `?? ""` défensif qui
 * ne pouvait jamais servir.
 */
type Formulaire = {
  libelle: string;
  type: "BANQUE" | "MOBILE_MONEY";
  banque: string;
  numeroCompte: string;
  titulaire: string;
  parDefaut: boolean;
};

const VIDE: Formulaire = {
  libelle: "",
  type: "BANQUE",
  banque: "",
  numeroCompte: "",
  titulaire: "",
  parDefaut: false,
};

/**
 * Le compte sur lequel l'école reçoit l'argent encaissé pour elle.
 *
 * Deux partis pris d'écran. D'abord le numéro reste masqué par défaut et ne
 * s'affiche en entier que sur demande : il traîne moins sous les yeux d'un
 * visiteur penché sur l'épaule de l'économe. Ensuite un compte fraîchement
 * saisi n'est pas « confirmé » : il faut un second geste, explicite, après
 * relecture — parce qu'au chargement initial trois écoles se sont retrouvées
 * avec le même numéro de banque, recopié d'une fiche à l'autre.
 */
export default function EcoleCompteReversement() {
  const [complet, setComplet] = useState(false);
  const { data: comptes, isLoading } = useListComptesReversement(complet);
  const [form, setForm] = useState<Formulaire | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const creer = useCreateCompteReversement();
  const modifier = useUpdateCompteReversement();
  const verifier = useVerifierCompteReversement();
  const desactiver = useDesactiverCompteReversement();

  const enregistrer = async (form: Formulaire) => {
    if (!form.libelle.trim() || !form.numeroCompte.trim()) {
      toast.error("Le libellé et le numéro de compte sont obligatoires.");
      return;
    }
    try {
      if (editId) {
        await modifier.mutateAsync({ id: editId, data: form });
        toast.success("Compte modifié. Il doit être confirmé à nouveau.");
      } else {
        const res = await creer.mutateAsync(form);
        if (res.autresEcolesMemeNumero.length > 0) {
          toast.warning(
            "Ce numéro est déjà déclaré par une autre école. Vérifiez-le avant de le confirmer.",
            { duration: 10000 },
          );
        } else {
          toast.success("Compte enregistré. Relisez-le puis confirmez-le.");
        }
      }
      setForm(null);
      setEditId(null);
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  const confirmer = async (c: CompteReversement) => {
    try {
      await verifier.mutateAsync(c.id);
      toast.success("Compte confirmé.");
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  const retirer = async (c: CompteReversement) => {
    if (!window.confirm(`Retirer le compte « ${c.libelle} » ? Il ne sera plus proposé.`)) return;
    try {
      await desactiver.mutateAsync(c.id);
      toast.success("Compte retiré.");
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  const enCours = creer.isPending || modifier.isPending;
  const liste = comptes ?? [];
  const aucunConfirme = liste.length > 0 && !liste.some((c) => c.verifie);

  return (
    <EcoleShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Compte de reversement</h1>
          <p className="text-muted-foreground mt-1">
            C'est sur ce compte que vous sera reversé l'argent payé par les familles.
            Tant qu'aucun compte n'est confirmé, aucun reversement ne peut être effectué.
          </p>
        </div>

        {liste.length === 0 && !isLoading && (
          <Card className="border-amber-300 bg-amber-50 p-4 text-amber-900">
            <div className="flex gap-3">
              <TriangleAlert className="h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">Aucun compte enregistré</p>
                <p className="text-sm">
                  Renseignez le compte bancaire ou mobile money de l'établissement.
                  Le titulaire doit être l'école, jamais une personne.
                </p>
              </div>
            </div>
          </Card>
        )}

        {aucunConfirme && (
          <Card className="border-amber-300 bg-amber-50 p-4 text-amber-900">
            <div className="flex gap-3">
              <TriangleAlert className="h-5 w-5 shrink-0" />
              <p className="text-sm">
                Votre compte est enregistré mais <strong>pas encore confirmé</strong>.
                Relisez le numéro chiffre par chiffre, puis cliquez sur « Confirmer ».
              </p>
            </div>
          </Card>
        )}

        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setComplet((v) => !v)}>
            <Eye className="mr-2 h-4 w-4" />
            {complet ? "Masquer les numéros" : "Afficher les numéros en entier"}
          </Button>
          {!form && (
            <Button onClick={() => { setForm({ ...VIDE }); setEditId(null); }}>
              <Plus className="mr-2 h-4 w-4" />
              Ajouter un compte
            </Button>
          )}
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        <div className="grid gap-3">
          {liste.map((c) => (
            <Card key={c.id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex gap-3">
                  {c.type === "MOBILE_MONEY"
                    ? <Smartphone className="mt-1 h-5 w-5 text-muted-foreground" />
                    : <Landmark className="mt-1 h-5 w-5 text-muted-foreground" />}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.libelle}</span>
                      {c.parDefaut && (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                          compte principal
                        </span>
                      )}
                      {c.verifie ? (
                        <span className="flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                          <ShieldCheck className="h-3 w-3" /> confirmé
                        </span>
                      ) : (
                        <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                          à confirmer
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-mono text-sm">{c.numeroCompte}</p>
                    <p className="text-sm text-muted-foreground">
                      {[c.banque, c.titulaire].filter(Boolean).join(" · ") || "—"}
                    </p>
                    {c.source && (
                      <p className="mt-1 text-xs text-muted-foreground">Source : {c.source}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {!c.verifie && (
                    <Button size="sm" onClick={() => confirmer(c)} disabled={verifier.isPending}>
                      Confirmer
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditId(c.id);
                      setForm({
                        libelle: c.libelle, type: c.type, banque: c.banque ?? "",
                        numeroCompte: complet ? c.numeroCompte : "",
                        titulaire: c.titulaire ?? "", parDefaut: c.parDefaut,
                      });
                    }}
                  >
                    Modifier
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => retirer(c)}>
                    Retirer
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {form && (
          <Card className="space-y-4 p-4">
            <h2 className="font-medium">{editId ? "Modifier le compte" : "Nouveau compte"}</h2>
            {editId && !complet && (
              <p className="rounded bg-amber-50 p-2 text-sm text-amber-900">
                Le numéro n'est pas affiché en entier : saisissez-le à nouveau, ou revenez
                après avoir cliqué sur « Afficher les numéros en entier ».
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">Nom du compte</span>
                <Input
                  value={form.libelle}
                  placeholder="Compte principal"
                  onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">Type</span>
                <select
                  className="h-9 w-full rounded-md border px-3 text-sm"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as "BANQUE" | "MOBILE_MONEY" })}
                >
                  <option value="BANQUE">Compte bancaire</option>
                  <option value="MOBILE_MONEY">Mobile money</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">
                  {form.type === "MOBILE_MONEY" ? "Opérateur" : "Banque"}
                </span>
                <Input
                  value={form.banque}
                  placeholder={form.type === "MOBILE_MONEY" ? "Orange Money, Wave…" : "ECOBANK, BNI…"}
                  onChange={(e) => setForm({ ...form, banque: e.target.value })}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">
                  {form.type === "MOBILE_MONEY" ? "Numéro de téléphone" : "Numéro de compte (RIB)"}
                </span>
                <Input
                  value={form.numeroCompte}
                  className="font-mono"
                  onChange={(e) => setForm({ ...form, numeroCompte: e.target.value })}
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block text-muted-foreground">
                  Titulaire du compte — le nom de l'établissement
                </span>
                <Input
                  value={form.titulaire}
                  onChange={(e) => setForm({ ...form, titulaire: e.target.value })}
                />
              </label>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.parDefaut}
                  onChange={(e) => setForm({ ...form, parDefaut: e.target.checked })}
                />
                Compte principal de l'établissement
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => enregistrer(form)} disabled={enCours}>
                {enCours && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Enregistrer
              </Button>
              <Button variant="ghost" onClick={() => { setForm(null); setEditId(null); }}>
                Annuler
              </Button>
            </div>
          </Card>
        )}
      </div>
    </EcoleShell>
  );
}
